import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import type { PackChoice } from "@/lib/classes/deck-drafter/DeckDraftClientEngine";
import type { Unit } from "@/lib/types/unit";
import { Deck } from "@/lib/classes/deck";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { getUnitImagePath } from "@/lib/utils/unit-images";
import { UnitInfoDialog } from "@/components/DeckBuilder/UnitInfoDialog";
import { useComparisonModals } from "@/components/DeckBuilder/ComparisonModalManager";
import { Info } from "lucide-react";
import { UnitCategory } from "@/lib/types/deck-builder";
import { filterTraits, getTraitIconPath } from "@/lib/utils/trait-icons";
import { useDataStore } from "@/lib/store/data-store";
import { WeaponImage } from "@/components/WeaponImage/WeaponImage";
import { Tooltip, TooltipTrigger, TooltipContent } from "@/components/ui/tooltip";
import { getTraitDisplayName, getTraitDescription } from "@/lib/utils/trait-names";

interface UnitPickerProps {
  choices: PackChoice[];
  units: Unit[];
  deck: Deck | null;
  onUnitChosen: (choice: number) => void;
  disabled?: boolean;
}

// Helper function to get category code from category enum
function getCategoryCode(category: UnitCategory): string {
  const codeMap: Record<UnitCategory, string> = {
    [UnitCategory.LOG]: "LOG",
    [UnitCategory.INF]: "INF",
    [UnitCategory.ART]: "ART",
    [UnitCategory.TNK]: "TNK",
    [UnitCategory.REC]: "REC",
    [UnitCategory.AA]: "AA",
    [UnitCategory.HEL]: "HEL",
    [UnitCategory.AIR]: "AIR",
    [UnitCategory.DEFENSE]: "DEF",
  };
  return codeMap[category] || category.split("/").pop() || "";
}

import { displayOptics } from "@/lib/utils/display-optics";
import { displayStealth } from "@/lib/utils/display-stealth";

export function UnitPicker({
  choices,
  units,
  deck,
  onUnitChosen,
  disabled = false,
}: UnitPickerProps) {
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);
  const [isAnimating, setIsAnimating] = useState(false);
  const [infoDialogOpen, setInfoDialogOpen] = useState<{ [key: number]: boolean }>({});
  const [infoDialogUnit, setInfoDialogUnit] = useState<Unit | null>(null);
  const [traitImageErrors, setTraitImageErrors] = useState<{ [key: string]: Set<number> }>({});
  const { weapons, getWeaponByDescriptor, fetchWeapons, loadingWeapons } = useDataStore();
  const { openComparisonModal } = useComparisonModals();

  // Ensure weapons are loaded
  useEffect(() => {
    if (!weapons && !loadingWeapons) {
      fetchWeapons();
    }
  }, [weapons, loadingWeapons, fetchWeapons]);

  const handleChoose = (index: number) => {
    if (disabled || isAnimating) return;
    
    setSelectedIndex(index);
    setIsAnimating(true);
    
    // Delay the callback to allow animation to play
    setTimeout(() => {
      onUnitChosen(index);
      setSelectedIndex(null);
      setIsAnimating(false);
    }, 300);
  };

  const handleInfoClick = (unit: Unit, index: number, e: React.MouseEvent) => {
    e.stopPropagation();
    // On desktop, open comparison modal directly; on mobile, use regular modal
    if (typeof window !== "undefined" && window.innerWidth >= 1024) {
      openComparisonModal(unit);
    } else {
      setInfoDialogUnit(unit);
      setInfoDialogOpen({ ...infoDialogOpen, [index]: true });
    }
  };

  const handleInfoDialogClose = (index: number) => {
    setInfoDialogOpen({ ...infoDialogOpen, [index]: false });
  };

  const handleTraitImageError = (unitId: string, index: number) => {
    setTraitImageErrors(prev => ({
      ...prev,
      [unitId]: new Set(prev[unitId] || []).add(index)
    }));
  };

  if (choices.length === 0) {
    return null;
  }

  return (
    <div className="container mx-auto px-4 py-2">
      <motion.div
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
        className="mb-2 text-center"
      >
        <h2 className="text-3xl font-bold">Choose a Unit</h2>
      </motion.div>

      <div className="overflow-x-auto pb-4 -mx-4 px-4">
        <div className="flex flex-nowrap justify-start items-stretch gap-6 min-w-max max-w-6xl mx-auto">
          <AnimatePresence mode="wait">
            {choices.map((choice, index) => {
              const unit = units.find((u) => u.descriptorName === choice.unit);
              const transport = choice.transport
                ? units.find((u) => u.descriptorName === choice.transport)
                : null;

              if (!unit || !deck) return null;

              const pack = deck.getPackForUnitDescriptor(choice.unit);
              if (!pack) return null;

              const isSelected = selectedIndex === index;
              const shouldHide = selectedIndex !== null && selectedIndex !== index;

              // Get veterancy quantity
              const veterancyQuantities = Deck.getVeterancyQuantitiesForPack(pack, unit);
              const quantity = veterancyQuantities[choice.veterancy] || 0;

              // Calculate unit cost (per individual unit, excluding transport)
              const unitCost = unit.commandPoints || 0;
              const transportCost = transport?.commandPoints || 0;
              const totalCost = unitCost;

              const veterancyLabels = ["REGULAR", "HARDENED", "VETERAN", "ELITE"];

              // Get category and slot cost
              const category = unit.factoryDescriptor as UnitCategory;
              const slotCost = deck.getNextSlotCostForCategory(category);

              // Get traits for overlay
              const traits = filterTraits(unit.specialities || []);
              const unitId = `${unit.descriptorName}-${index}`;
              const traitErrors = traitImageErrors[unitId] || new Set<number>();

              return (
                <motion.div
                  key={`${choice.unit}-${index}`}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{
                    opacity: shouldHide ? 0 : 1,
                    scale: isSelected ? 1.02 : 1,
                    y: 0,
                  }}
                  exit={{ opacity: 0 }}
                  transition={{
                    duration: 0.3,
                    delay: index * 0.05,
                  }}
                  className="flex-shrink-0 w-[320px] sm:w-[360px] md:w-[380px]"
                >
                <Card
                  className={`transition-all duration-200 relative overflow-hidden py-0 h-full ${
                    isSelected
                      ? "ring-2 ring-primary"
                      : "hover:border-primary/50"
                  } ${shouldHide ? "pointer-events-none" : ""}`}
                >
                  <CardContent className="p-0 flex flex-col h-full">
                    {/* Unit Image with Overlays */}
                    <div className="relative w-full flex items-center justify-center overflow-hidden">
                      {/* Header: Veterancy (subtle, top-left) */}
                      <div className="absolute top-2 left-2 z-10">
                        <div className="text-sm font-semibold uppercase text-white/90 bg-black/70 backdrop-blur-sm px-2 py-1 rounded">
                          {veterancyLabels[choice.veterancy] || `LEVEL ${choice.veterancy}`}
                        </div>
                      </div>
                      <img
                        src={getUnitImagePath(unit.descriptorName)}
                        alt={unit.name}
                        className="w-full h-full object-contain"
                        onError={(e) => {
                          const target = e.target as HTMLImageElement;
                          target.style.display = "none";
                          const parent = target.parentElement;
                          if (parent) {
                            const placeholder = document.createElement("div");
                            placeholder.className = "w-full h-full flex items-center justify-center bg-muted";
                            placeholder.innerHTML = '<span class="text-sm text-muted-foreground">Unit Image</span>';
                            parent.appendChild(placeholder);
                          }
                        }}
                      />


                      {/* Info Button (top-right) */}
                      {!disabled && !shouldHide && (
                        <button
                          onClick={(e) => handleInfoClick(unit, index, e)}
                          className="absolute top-2 right-2 w-7 h-7 bg-black/70 hover:bg-black/90 rounded-full flex items-center justify-center transition-colors z-20 shadow-lg"
                          aria-label="Unit information"
                        >
                          <Info className="h-4 w-4 text-white" />
                        </button>
                      )}

                      {/* Traits Icons (right side, vertical stack) */}
                      {traits.length > 0 && (
                        <div className="absolute right-2 top-1/2 -translate-y-1/2 flex flex-col gap-1.5 z-10">
                          {traits.slice(0, 4).map((trait, traitIndex) => {
                            const iconPath = getTraitIconPath(trait);
                            const hasError = traitErrors.has(traitIndex);
                            const displayName = getTraitDisplayName(trait);
                            const description = getTraitDescription(trait);
                            
                            return (
                              <Tooltip key={trait}>
                                <TooltipTrigger asChild>
                                  <div className="w-6 h-6 bg-black/70 rounded flex items-center justify-center cursor-help">
                                    {iconPath && !hasError ? (
                                      <img
                                        src={iconPath}
                                        alt={displayName}
                                        className="w-4 h-4 object-contain"
                                        style={{ filter: 'var(--trait-icon-filter)' }}
                                        onError={() => handleTraitImageError(unitId, traitIndex)}
                                      />
                                    ) : (
                                      <span className="text-[10px] text-primary font-bold">
                                        {trait.replace('_', '').charAt(0).toUpperCase()}
                                      </span>
                                    )}
                                  </div>
                                </TooltipTrigger>
                                <TooltipContent className="max-w-xs">
                                  <div className="space-y-1">
                                    <div className="font-semibold">{displayName}</div>
                                    <div className="text-sm">{description}</div>
                                  </div>
                                </TooltipContent>
                              </Tooltip>
                            );
                          })}
                        </div>
                      )}

                      {/* Quantity Indicator (bottom-left) */}
                      <div className="absolute bottom-2 left-2 bg-black/80 backdrop-blur-sm px-2.5 py-1.5 rounded text-white text-sm font-semibold z-10">
                        x{quantity}
                      </div>

                      {/* Cost Indicator (bottom-right) */}
                      <div className="absolute bottom-2 right-2 bg-black/80 backdrop-blur-sm px-2.5 py-1.5 rounded text-white text-sm font-semibold z-10">
                        {totalCost} PTS
                      </div>
                    </div>

                    {/* Unit Name and Badges */}
                    <div className="px-4 pt-4 pb-3 border-b">
                      <div className="flex items-center gap-2 justify-center flex-wrap">
                        <h3 className="text-xl font-semibold text-center">
                          {unit.name}
                        </h3>
                        {unit.isCommand && (
                          <Badge variant="secondary" className="text-sm px-2 py-0.5">Cmd</Badge>
                        )}
                        {unit.isSpecialForces && (
                          <Badge variant="secondary" className="text-sm px-2 py-0.5">SF</Badge>
                        )}
                      </div>
                    </div>

                    {/* Armor Display */}
                    <div className="px-4 py-3 border-b">
                      <div className="grid grid-cols-4 gap-2">
                        <div className="text-center">
                          <div className="text-lg font-bold">{unit.frontArmor}</div>
                          <div className="text-xs text-muted-foreground">Front</div>
                        </div>
                        <div className="text-center">
                          <div className="text-lg font-bold">{unit.sideArmor}</div>
                          <div className="text-xs text-muted-foreground">Side</div>
                        </div>
                        <div className="text-center">
                          <div className="text-lg font-bold">{unit.rearArmor}</div>
                          <div className="text-xs text-muted-foreground">Rear</div>
                        </div>
                        <div className="text-center">
                          <div className="text-lg font-bold">{unit.topArmor}</div>
                          <div className="text-xs text-muted-foreground">Top</div>
                        </div>
                      </div>
                    </div>

                    {/* Weapon Icons */}
                    <div className="px-4 border-b flex items-center justify-center gap-4 bg-muted/20 min-h-[80px]">
                      {unit.weaponDescriptorName && (() => {
                        const weaponData = getWeaponByDescriptor(unit.weaponDescriptorName);
                        if (weaponData && weaponData.weapons && weaponData.weapons.length > 0) {
                          return weaponData.weapons.slice(0, 4).map((weapon, weaponIndex) => {
                            if (!weapon.ammo.textureId) return null;
                            
                            return (
                              <div
                                key={weaponIndex}
                                className="flex flex-col items-center justify-center"
                                title={weapon.ammo.name}
                              >
                                <div className="h-14 w-14 flex items-center justify-center">
                                  <WeaponImage
                                    textureId={weapon.ammo.textureId}
                                    alt={weapon.ammo.name}
                                    size="custom"
                                    className="h-full w-auto"
                                    showPlaceholder={false}
                                  />
                                </div>
                              </div>
                            );
                          });
                        }
                        return null;
                      })()}
                    </div>

                    {/* Key Stats - Compact Grid */}
                    <div className="px-4 py-3 grid grid-cols-2 gap-x-4 gap-y-2.5 text-sm border-b">
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Speed:</span>
                        <span className="font-medium">{unit.speed} km/h</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Optics:</span>
                        <span className="font-medium">{displayOptics(unit.optics)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Stealth:</span>
                        <span className="font-medium">{displayStealth(unit.stealth)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Strength:</span>
                        <span className="font-medium">{unit.maxDamage}</span>
                      </div>
                    </div>

                    {/* Transport Section */}
                    {transport && (
                      <div className="px-4 py-2 border-b bg-muted/30">
                        <div className="text-sm mb-1.5">
                          <span className="text-muted-foreground">Transport: </span>
                          <span className="font-medium">{transport.name}</span>
                          <span className="text-muted-foreground ml-2">({transportCost} CP)</span>
                        </div>
                        <div className="relative w-full h-16 flex items-center justify-center overflow-hidden rounded border border-border/50">
                          <img
                            src={getUnitImagePath(transport.descriptorName)}
                            alt={transport.name}
                            className="w-full h-full object-contain"
                            onError={(e) => {
                              const target = e.target as HTMLImageElement;
                              target.style.display = "none";
                              const parent = target.parentElement;
                              if (parent) {
                                const placeholder = document.createElement("div");
                                placeholder.className = "w-full h-full flex items-center justify-center bg-muted";
                                placeholder.innerHTML = '<span class="text-xs text-muted-foreground">Transport Image</span>';
                                parent.appendChild(placeholder);
                              }
                            }}
                          />
                        </div>
                      </div>
                    )}

                    {!transport && pack.availableWithoutTransport && (
                      <div className="px-4 py-2.5 border-b bg-muted/30">
                        <div className="text-sm text-muted-foreground">No Transport</div>
                      </div>
                    )}

                    {/* Slot Cost and Add Button Section */}
                    <div className="px-4 py-3 flex items-center justify-between mt-auto">
                      {slotCost !== undefined && (
                        <div className="text-sm">
                          <span className="text-muted-foreground">Slot: </span>
                          <span className="font-semibold">
                            {getCategoryCode(category)} {slotCost}
                          </span>
                        </div>
                      )}
                      {!disabled && !shouldHide && (
                        <Button
                          onClick={() => handleChoose(index)}
                          disabled={isAnimating}
                          className="ml-auto"
                        >
                          Add
                        </Button>
                      )}
                    </div>
                  </CardContent>
                </Card>

                {/* Info Dialog */}
                {infoDialogUnit && infoDialogOpen[index] && (
                  <UnitInfoDialog
                    open={infoDialogOpen[index]}
                    onOpenChange={(open) => {
                      if (!open) {
                        handleInfoDialogClose(index);
                      }
                    }}
                    unit={infoDialogUnit}
                  />
                )}
              </motion.div>
            );
          })}
          </AnimatePresence>
        </div>
      </div>
    </div>
  );
}
